import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, inr, requestErrorMessage } from "@/lib/erp";
import { PCONF } from "@/lib/erpdocs";
import { useActiveColumns, alignClass } from "@/components/ActiveColumns";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, ShoppingCart, Eye, Trash2, Printer, MessageCircle, Mail } from "lucide-react";
import { shareBillWhatsApp, shareBillEmail } from "@/lib/sharebill";
import { useWhatsAppSender } from "@/context/WhatsAppSendContext";
import { toast } from "sonner";

const PSHARE = { phoneKey: "vendor_phone", emailKey: "vendor_email", nameKey: "vendor_name", noKey: "doc_no", totalKey: "grand_total", label: "Purchase" };

export default function Purchases({ dtype }) {
  const requestWhatsAppSend = useWhatsAppSender();
  const cfg = PCONF[dtype];
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loadError, setLoadError] = useState("");
  const navigate = useNavigate();

  const cols = [
    { key: "doc_no", label: "Doc No", cell: (p) => <span className="font-mono font-medium cursor-pointer hover:text-primary" onClick={() => navigate(`${cfg.base}/${p.id}`)}>{p.doc_no}</span> },
    ...(cfg.showBill ? [{ key: "bill_no", label: "Bill No", cell: (p) => <span className="font-mono text-xs">{p.bill_no || "—"}</span> }] : []),
    { key: "bill_date", label: "Date", cell: (p) => <span className="text-muted-foreground">{p.bill_date}</span> },
    { key: "vendor_name", label: "Vendor", cell: (p) => p.vendor_name },
    ...(dtype === "purchase_invoice" ? [
      { key: "due_date", label: "Due Date", cell: (p) => <span className="text-muted-foreground">{p.due_date || "—"}</span> },
      { key: "credit_days_snapshot", label: "Credit Days", cell: (p) => p.credit_days_snapshot == null ? "—" : p.credit_days_snapshot === 0 ? "Immediate" : `${p.credit_days_snapshot} days` },
    ] : []),
    { key: "grand_total", label: "Amount", align: "right", cell: (p) => <span className="font-mono">{inr(p.grand_total)}</span> },
    ...(cfg.showPaid ? [{ key: "status", label: "Status", align: "center", cell: (p) => <StatusBadge status={p.status} /> }] : []),
  ];
  const { shown, Picker } = useActiveColumns(`purchase_${cfg.api}`, cols);

  const load = async () => {
    try { const r = await http.get(`/purchases/${cfg.api}`, { params: { search } }); setRows(r.data); setLoadError(""); }
    catch (e) { setLoadError(requestErrorMessage(e, `load ${cfg.plural.toLowerCase()}`)); }
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, dtype]);
  const del = async (id) => {
    if (!window.confirm("Delete?")) return;
    try { await http.delete(`/purchases/${cfg.api}/${id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(requestErrorMessage(e, "delete purchase")); }
  };

  return (
    <div id="print-area" className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{cfg.plural}</h1><p className="text-sm text-muted-foreground">{rows.length} records{dtype === "purchase_invoice" ? " · adds stock" : dtype === "purchase_return" ? " · removes stock" : ""}</p></div>
        <Button onClick={() => navigate(`${cfg.base}/new`)} data-testid="create-purchase-button"><Plus className="h-4 w-4 mr-1" /> New {cfg.label}</Button>
      </div>
      <Card className="p-0 overflow-hidden">
        {loadError && <div className="flex items-center justify-between gap-3 border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"><span>{loadError}</span><Button size="sm" variant="outline" onClick={load}>Retry</Button></div>}
        <div className="no-print p-4 border-b flex items-center gap-3 flex-wrap">
          <div className="relative max-w-sm flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="purchase-search" /></div>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="print" onClick={() => window.print()} data-testid="print-list-button"><Printer className="h-4 w-4 mr-1" /> Print</Button>
            {Picker}
          </div>
        </div>
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead className="bg-slate-50"><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground">
            {shown.map((c) => <th key={c.key} className={`px-4 py-3 font-medium ${alignClass(c.align)}`}>{c.label}</th>)}
            <th className="px-4 py-3 font-medium text-right no-print">Actions</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (<tr key={p.id} className="border-t hover:bg-slate-50" data-testid={`purchase-row-${p.id}`}>
              {shown.map((c) => <td key={c.key} className={`px-4 py-3 ${alignClass(c.align)}`}>{c.cell(p)}</td>)}
              <td className="px-4 py-3 no-print"><div className="flex justify-end gap-1"><button onClick={() => shareBillWhatsApp(`/purchases/${cfg.api}/${p.id}`, PSHARE, requestWhatsAppSend)} className="p-1.5 hover:bg-emerald-50 rounded" data-testid={`wa-${p.id}`}><MessageCircle className="h-4 w-4 text-emerald-600" /></button><button onClick={() => shareBillEmail(`/purchases/${cfg.api}/${p.id}`, PSHARE)} className="p-1.5 hover:bg-slate-100 rounded" data-testid={`email-${p.id}`}><Mail className="h-4 w-4 text-brand-600" /></button><button onClick={() => navigate(`${cfg.base}/${p.id}`)} className="p-1.5 hover:bg-slate-100 rounded"><Eye className="h-4 w-4 text-slate-600" /></button><button onClick={() => del(p.id)} className="p-1.5 hover:bg-rose-50 rounded"><Trash2 className="h-4 w-4 text-rose-500" /></button></div></td>
            </tr>))}
            {rows.length === 0 && <tr><td colSpan={shown.length + 1} className="px-4 py-12 text-center text-muted-foreground"><ShoppingCart className="h-8 w-8 mx-auto mb-2 opacity-40" />No records</td></tr>}
          </tbody>
        </table></div>
      </Card>
    </div>
  );
}
